import React, { ReactNode, useEffect, useState } from 'react';
import DayPicker, { Modifiers, RangeModifier } from 'react-day-picker';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { Tooltip } from 'components';
import { appConfig } from 'config/app';
import { setCurrentDateRange } from 'store/App';
import { Icon } from '../Icon';
import {
  CloseOverlay,
  InputDivider,
  InputEnd,
  InputHeightOptions,
  InputStart,
  InputWrap,
  InputWrapSubmit,
  Layout,
  PickerContainer,
  PickerFooter,
  PickerFooterPresets,
  PickerPositionOptions,
  PresetButton,
  SubmitButton,
} from './DateRangePickerStyles';
import { RangeClassName } from './ReactDayPickerStyles';

interface IDateRangePickerProps {
  className?: string;
  dividerLabel?: string | ReactNode;
  fluidWidth?: boolean;
  inputHeight?: InputHeightOptions;
  onSubmit: (dates: string[]) => void;
  pickerPosition?: PickerPositionOptions;
  submitLabel?: string;
  startDate?: string;
  endDate?: string;
  dataCy?: string;
  maxDays?: number;
  setGlobalDateRange?: boolean;
}

const DateRangePicker = ({
  className,
  dividerLabel = 'to',
  fluidWidth,
  inputHeight = 'large',
  onSubmit,
  pickerPosition = 'right',
  submitLabel = 'Submit',
  startDate,
  endDate,
  maxDays = appConfig.DATE_RANGE.MAX_DAYS_YEAR,
  setGlobalDateRange = true,
  dataCy = 'date-range-picker',
}: IDateRangePickerProps) => {
  const dispatch = useDispatch();
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  const [pickerVisible, setPickerVisible] = useState<boolean>(false);

  // Inputs
  const [selectedInput, setSelectedInput] = useState<string>('from');
  const [dateRange, setDateRange] = useState<RangeModifier>({
    from: dayjs(startDate || dayjs().subtract(7, 'days'), ['YYYY-MM-DD', 'MM-DD-YYYY']).toDate(),
    to: dayjs(endDate || dayjs(), ['YYYY-MM-DD', 'MM-DD-YYYY']).toDate(),
  });
  const [prevDateRange, setPrevDateRange] = useState<RangeModifier>(dateRange);

  const handleStartDateFocus = () => {
    setSelectedInput('from');
    setInputFocused(true);
    setPickerVisible(true);
  };

  const handleEndDateFocus = () => {
    setSelectedInput('to');
    setInputFocused(true);
    setPickerVisible(true);
  };

  // Submission and Closing
  const handleSubmit = (dates: RangeModifier) => {
    const dateRange = [dayjs(dates.from || dayjs().subtract(7, 'days').toDate()).format('YYYY-MM-DD'), dayjs(dates.to || dayjs().toDate()).format('YYYY-MM-DD')];

    // Set global date range
    if (setGlobalDateRange) {
      dispatch(setCurrentDateRange(dateRange[0], dateRange[1]));
    }

    setPrevDateRange(dates);
    onSubmit(dateRange);
    setPickerVisible(false);
    setInputFocused(false);
  };

  // Submission Preset and Closing
  const handlePresetSubmit = (from: Date, to: Date) => {
    setDateRange({ ...dateRange, from, to });
    handleSubmit({ from, to });
  };

  const handleCloseOverlay = () => {
    setPickerVisible(false);
    setInputFocused(false);
  };

  const handleDayClick = (day: Date) => {
    let range = dateRange;

    // Set the from date.
    if (selectedInput === 'from') {
      range = { ...range, from: day };
      setSelectedInput('to');
    }

    // set the to date
    if (selectedInput === 'to') {
      if (day && range.from && day >= range.from) {
        range = { ...range, to: day };
      } else {
        range = { ...range, from: day };
      }
    }

    setDateRange(range);
  };

  useEffect(() => {
    if (!pickerVisible && !isEqual(dateRange, prevDateRange)) {
      setDateRange(prevDateRange);
    }
  }, [pickerVisible]);

  const { from, to } = dateRange;
  const modifiers = { start: from, end: to } as Partial<Modifiers>;

  const maxRangeExceeded = dayjs(to).diff(dayjs(from).startOf('day'), 'days', true) > maxDays;

  return (
    <>
      <Layout className={className} fluidWidth={fluidWidth} data-cy={dataCy}>
        <InputWrap inputFocused={inputFocused} inputHeight={inputHeight} pickerVisible={pickerVisible}>
          <InputStart
            data-cy={`${dataCy}-start-date`}
            fluidWidth={fluidWidth}
            onFocus={handleStartDateFocus}
            placeholder="Start Date"
            value={dateRange.from?.toLocaleDateString()}
            className={pickerVisible && selectedInput === 'from' ? 'hasFocus' : ''}
            readOnly
          />
          <InputDivider>{dividerLabel}</InputDivider>
          <InputEnd
            data-cy={`${dataCy}-end-date`}
            fluidWidth={fluidWidth}
            placeholder="End Date"
            onFocus={handleEndDateFocus}
            value={dateRange.to?.toLocaleDateString()}
            className={pickerVisible && selectedInput === 'to' ? 'hasFocus' : ''}
            readOnly
          />
          <InputWrapSubmit aria-label="Click to submit new date range." onClick={() => handleSubmit(dateRange)} disabled={maxRangeExceeded} data-cy={`${dataCy}-icon-submit-btn`}>
            <Icon icon="Calendar" tooltipId="max-range-exceeded" />
          </InputWrapSubmit>
        </InputWrap>

        {pickerVisible && (
          <PickerContainer inputHeight={inputHeight} pickerPosition={pickerPosition} data-cy={`${dataCy}-calendar`}>
            <DayPicker className={`${RangeClassName} notranslate`} month={from || dayjs().toDate()} toMonth={dayjs().toDate()} numberOfMonths={2} selectedDays={dateRange} modifiers={modifiers} onDayClick={handleDayClick} />
            <PickerFooter hasPresets>
              <PickerFooterPresets>
                <PresetButton onClick={() => handlePresetSubmit(dayjs().toDate(), dayjs().toDate())} data-cy={`${dataCy}-preset-today`}>
                  Today
                </PresetButton>
                <PresetButton onClick={() => handlePresetSubmit(dayjs().subtract(6, 'days').toDate(), dayjs().toDate())} data-cy={`${dataCy}-preset-7-days`}>
                  Last 7
                </PresetButton>
                <PresetButton onClick={() => handlePresetSubmit(dayjs().subtract(29, 'days').toDate(), dayjs().toDate())} data-cy={`${dataCy}-preset-30-days`}>
                  Last 30
                </PresetButton>
                <PresetButton onClick={() => handlePresetSubmit(dayjs().subtract(89, 'days').toDate(), dayjs().toDate())} data-cy={`${dataCy}-preset-90-days`}>
                  Last 90
                </PresetButton>
              </PickerFooterPresets>

              <SubmitButton label={submitLabel} aria-label="Click to submit new date range." onClick={() => handleSubmit(dateRange)} disabled={maxRangeExceeded} tooltipId="max-range-exceeded" data-cy={`${dataCy}-submit-btn`} />
            </PickerFooter>
          </PickerContainer>
        )}

        {maxRangeExceeded && <Tooltip id="max-range-exceeded">Max date range exceeded</Tooltip>}
      </Layout>

      {pickerVisible && <CloseOverlay data-cy={`${dataCy}-close-overlay`} onClick={handleCloseOverlay} />}
    </>
  );
};

export default DateRangePicker;
